import { CommercialLink, DealerType } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { UserRole } from 'domains/user';
import { Country } from 'utils/i18n/Country';

export const TRACTION_BATTERY_FAMILY_CODES = ['277M', '281R'] as const;
const TRACTION_BATTERY_FAMILY_CODE_SET = new Set<string>(TRACTION_BATTERY_FAMILY_CODES);
export const SINGLE_UNIT_ORDER_LIMIT = 1;
export const SINGLE_UNIT_RESTRICTED_FAMILY_CODES = TRACTION_BATTERY_FAMILY_CODES;
const SINGLE_UNIT_RESTRICTED_FAMILY_CODE_SET = new Set<string>(SINGLE_UNIT_RESTRICTED_FAMILY_CODES);

const TRACTION_BATTERY_ROLE_BLOCKED_SET = new Set<UserRole>(['R3', 'R3_MOTRIO'] as UserRole[]);
const TRACTION_BATTERY_VIN_REQUIRED_ROLE_SET = new Set<UserRole>([
  'R1',
  'R2',
  'COMMAND',
  'CONNECT_COMMANDE',
] as UserRole[]);

export type TractionBatteryOrderBlockReason =
  | 'r3_restricted'
  | 'ze_ready_required'
  | 'vin_required'
  | 'vin_non_compliant';

export const hasUserAnyRight = (possibleRights: UserRole[], userRights?: UserRole[]): boolean => {
  if (possibleRights.length === 0) {
    return true;
  }
  return possibleRights.some((pr) => userRights?.includes(pr));
};

export const doesDealerTypeMatch = (possibleDealerTypes: DealerType[], dealerType?: DealerType): boolean => {
  if (possibleDealerTypes.length === 0) {
    return true;
  }
  return possibleDealerTypes.some((pd) => dealerType === pd);
};

export const doesUserBelongToCountry = (possibleCountries: Country[], userCountry?: Country): boolean => {
  if (possibleCountries.length === 0) {
    return true;
  }
  return possibleCountries.some((pc) => userCountry?.key === pc.key);
};

export const isOEMAllowed = (
  commercialLink: CommercialLink | null,
  userCountry: string | undefined,
  userRights?: UserRole[],
): boolean => {
  return (
    [Country.FR.key, Country.IT.key, Country.ES.key].includes(commercialLink?.countryCode ?? userCountry ?? '') &&
    hasUserAnyRight(
      [
        UserRole.IAM_LIGHT_ACCESS,
        UserRole.IAM_ACCESS,
        UserRole.IAM_FAST_ACCESS,
        UserRole.IAM_PRO_ACCESS,
        UserRole.IAM_PRO_PLUS_ACCESS,
      ],
      userRights,
    )
  );
};

export const isZEOrderingTier = (userRights: UserRole[]): boolean => {
  return (
    hasUserAnyRight([UserRole.R1, UserRole.R2], userRights) &&
    hasUserAnyRight([UserRole.COMMAND, UserRole.CONNECT_COMMANDE], userRights)
  );
};

export const isZEReady = (userRights: UserRole[]): boolean => {
  return hasUserAnyRight([UserRole.ZE_READY], userRights);
};

export const isVirtualStoreBamAllowed = (userCountry?: string, userRights?: UserRole[]): boolean =>
  [Country.FR.key].includes(userCountry ?? '') && hasUserAnyRight([UserRole.R1], userRights);

export const isTrophyAdvantagesAllowed = (userCountry?: string, userRights?: UserRole[]): boolean => {
  return (
    [Country.FR.key].includes(userCountry ?? '') &&
    hasUserAnyRight([UserRole.R1, UserRole.R2, UserRole.R3, UserRole.R3_MOTRIO], userRights)
  );
};

export const isMotrioEcosystemAllowed = (userCountry?: string, userRights?: UserRole[]): boolean =>
  [Country.FR.key].includes(userCountry ?? '') && hasUserAnyRight([UserRole.R3_MOTRIO], userRights);

export const hasUserR3WithSubscriptionL234 = (userRights?: UserRole[]): boolean => {
  return (
    hasUserAnyRight([UserRole.R3, UserRole.R3_MOTRIO], userRights) &&
    hasUserAnyRight([UserRole.IAM_FAST_ACCESS, UserRole.IAM_PRO_ACCESS, UserRole.IAM_PRO_PLUS_ACCESS], userRights)
  );
};

export const hasUserR3WithLimitedAccessSubscriptionL01 = (userRights?: UserRole[]): boolean => {
  return (
    hasUserAnyRight([UserRole.R3, UserRole.R3_MOTRIO], userRights) &&
    hasUserAnyRight([UserRole.NO_IAM_ACCESS, UserRole.IAM_LIGHT_ACCESS, UserRole.IAM_ACCESS], userRights)
  );
};

export const isR3MotrioUser = (userRights?: UserRole[]): boolean => {
  return hasUserAnyRight([UserRole.R3_MOTRIO], userRights);
};

const hasRoleFromSet = (userRoles: UserRole[], roleSet: Set<UserRole>): boolean =>
  userRoles.some((role) => roleSet.has(role));

const isVinMissing = (vin?: string): boolean => !vin?.trim();

export const isSingleUnitOrderRestrictedFamily = (familyCode?: string): boolean =>
  !!familyCode && SINGLE_UNIT_RESTRICTED_FAMILY_CODE_SET.has(familyCode);

export const getMaxOrderQuantityForFamily = (familyCode?: string, defaultQuantityLimit?: number): number | undefined =>
  isSingleUnitOrderRestrictedFamily(familyCode) ? SINGLE_UNIT_ORDER_LIMIT : defaultQuantityLimit;

export function getTractionBatteryOrderBlockReason(
  userRoles: UserRole[] | undefined,
  familyCode?: string,
  vin?: string,
  isCompatible = false,
): TractionBatteryOrderBlockReason | undefined {
  if (!familyCode || !userRoles || !TRACTION_BATTERY_FAMILY_CODE_SET.has(familyCode)) {
    return undefined;
  }

  if (hasRoleFromSet(userRoles, TRACTION_BATTERY_ROLE_BLOCKED_SET)) {
    return 'r3_restricted';
  }

  if (hasRoleFromSet(userRoles, TRACTION_BATTERY_VIN_REQUIRED_ROLE_SET) && !userRoles.includes(UserRole.ZE_READY)) {
    return 'ze_ready_required';
  }

  if (hasRoleFromSet(userRoles, TRACTION_BATTERY_VIN_REQUIRED_ROLE_SET) && isVinMissing(vin)) {
    return 'vin_required';
  }

  if (
    hasRoleFromSet(userRoles, TRACTION_BATTERY_VIN_REQUIRED_ROLE_SET) &&
    !isVinMissing(vin) &&
    isCompatible === false
  ) {
    return 'vin_non_compliant';
  }

  return undefined;
}

export function isTractionBatteryOrderBlocked(
  userRoles: UserRole[] | undefined,
  familyCode?: string,
  vin?: string,
  isCompatible = false,
): boolean {
  return !!getTractionBatteryOrderBlockReason(userRoles, familyCode, vin, isCompatible);
}
